/**
 * Iter XX.43 — Partner referral attribution client helpers.
 *
 * The player's relationship always belongs to Gully. This module only
 * stores a durable first-party cookie/localStorage hint so that when
 * the visitor signs up we can pass a referral hint to the backend
 * which then does the actual server-side attribution write.
 *
 * We never store partner secrets — only the public @username handle.
 */
import { credentialsMode } from "./nativePlatform";

const REF_COOKIE = "gully_ref";
const REF_TTL_DAYS = 60;

const API = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

function setCookie(name, value, days) {
  try {
    const exp = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    // Set on the root domain so /@ref and /join share the cookie.
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax`;
  } catch { /* no-op */ }
}

function readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
}

export function getStoredReferral() {
  const c = readCookie(REF_COOKIE);
  if (c) return c;
  try {
    return localStorage.getItem(REF_COOKIE);
  } catch {
    return null;
  }
}

export function storeReferral(hint) {
  if (!hint) return;
  const clean = String(hint).trim().replace(/^@/, "").toLowerCase();
  if (!clean) return;
  // First-touch — do NOT overwrite an existing cookie.
  if (readCookie(REF_COOKIE)) return;
  setCookie(REF_COOKIE, clean, REF_TTL_DAYS);
  try { localStorage.setItem(REF_COOKIE, clean); } catch { /* no-op */ }
}

export function clearReferral() {
  setCookie(REF_COOKIE, "", -1);
  try { localStorage.removeItem(REF_COOKIE); } catch { /* no-op */ }
}

/**
 * Fire the server-side "referral cookie set" analytics event and
 * validate that the referral resolves to a public partner. Returns
 * `{ok: true, partner: {...}}` when valid.
 */
export async function trackReferral({ ref, refCode }) {
  try {
    const r = await fetch(API("/api/partners/track"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: credentialsMode(),
      body: JSON.stringify({ ref: ref || null, ref_code: refCode || null }),
    });
    if (!r.ok) return { ok: false };
    return await r.json();
  } catch {
    return { ok: false };
  }
}

/** Log a public partner event (view / cta click / share / qr). */
export async function logPartnerEvent(username, event, extra = {}) {
  try {
    const body = { event, ...extra };
    await fetch(API(`/api/partners/public/${encodeURIComponent(username)}/event`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch { /* analytics never blocks UX */ }
}

/** Parse ?ref= from the current URL and lock it as first-touch. */
export async function captureReferralFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (!ref) return null;
    storeReferral(ref);
    const res = await trackReferral({ ref });
    return res?.partner || null;
  } catch {
    return null;
  }
}
